import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight } from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';
import { findBlogBySlug, BLOG_POSTS } from '@/content/blog';

const SITE = 'https://themeatworks.com.au';

export default function BlogArticle() {
  const { slug } = useParams();
  const post = findBlogBySlug(slug);

  const related = useMemo(() => {
    if (!post) return [];
    return BLOG_POSTS.filter((b) => b.slug !== slug).slice(0, 3);
  }, [post, slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: [SITE + post.heroImage],
    datePublished: post.dateISO,
    dateModified: post.dateISO,
    author: { '@type': 'Organization', name: post.author, url: SITE },
    publisher: {
      '@type': 'Organization',
      name: 'The Meat Works',
      url: SITE,
      logo: { '@type': 'ImageObject', url: `${SITE}/logo-md.webp` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}/blog/${post.slug}` },
    keywords: post.keywords,
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',    item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog',    item: `${SITE}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: `${SITE}/blog/${post.slug}` },
    ],
  };

  return (
    <div className="section-light App relative">
      <Seo
        title={post.title}
        description={post.excerpt}
        keywords={post.keywords}
        path={`/blog/${post.slug}`}
        image={SITE + post.heroImage}
        jsonLd={[jsonLd, breadcrumb]}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-14 bg-gradient-to-b from-[#FDFBF7] to-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <Link
            to="/blog"
            data-testid="blog-back"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-black/50 hover:text-black transition-colors mb-6"
          >
            <ArrowLeft size={12} /> All articles
          </Link>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
            {post.category} &middot; {post.readMinutes} min read
          </div>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-black">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-black/70 leading-relaxed">{post.subtitle}</p>
          <div className="mt-8 flex items-center gap-4 text-[10px] uppercase tracking-[0.22em] text-black/45">
            <span>{post.author}</span>
            <span className="text-black/25">·</span>
            <span>{new Date(post.dateISO).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <main className="bg-[#FAFAFA] pb-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <div className="relative aspect-[16/10] mb-14 overflow-hidden bg-black/5 border border-black/[0.06]">
            <img
              src={post.heroImage}
              alt={post.title}
              width="960"
              height="600"
              fetchpriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          <article className="prose-tmw">
            {post.body.map((block, i) => {
              if (block.type === 'heading') {
                return <h2 key={i} className="font-display text-2xl sm:text-3xl text-black mt-12 mb-4 leading-tight">{block.text}</h2>;
              }
              if (block.type === 'paragraph') {
                return <p key={i} className="text-black/80 leading-[1.75] mb-5 text-base sm:text-[17px]">{block.text}</p>;
              }
              if (block.type === 'list') {
                return (
                  <ul key={i} className="my-6 space-y-3">
                    {block.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3 text-black/80 leading-relaxed">
                        <span className="mt-2.5 w-1 h-1 rounded-full bg-[var(--mw-primary)] shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (block.type === 'quote') {
                return (
                  <blockquote key={i} className="my-10 border-l-2 border-[var(--mw-primary)] pl-6 italic text-black/70 text-xl sm:text-2xl leading-relaxed font-display">
                    {block.text}
                  </blockquote>
                );
              }
              return null;
            })}
          </article>

          {/* Closing CTA */}
          <div className="mt-16 p-8 bg-black text-white">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--mw-primary)] mb-3">Ready to talk trade?</div>
            <h3 className="font-display text-2xl leading-tight mb-4">Open a wholesale account with The Meat Works.</h3>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              Halal-certified Australian beef, lamb, poultry and smallgoods — cold-chain delivered across Perth
              and exported to Malaysia, Singapore and the UAE.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/wholesale/apply"
                data-testid="blog-cta-apply"
                style={{ color: '#fff' }}
                className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-[11px] uppercase tracking-[0.22em] px-6 py-3 transition-colors"
              >
                Apply for a trade account <ArrowRight size={14} />
              </Link>
              <Link
                to="/wholesale"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white text-[11px] uppercase tracking-[0.22em] px-6 py-3 transition-colors"
              >
                See wholesale pricing
              </Link>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-24 pt-14 border-t border-black/[0.08]">
              <h2 className="font-display text-2xl text-black mb-8">Continue reading</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group block">
                    <div className="relative aspect-[4/3] overflow-hidden bg-black/5 mb-3">
                      <img src={r.heroImage} alt={r.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.24em] text-[var(--mw-primary)] mb-1">{r.category}</div>
                    <h3 className="font-display text-lg text-black leading-tight group-hover:text-[var(--mw-primary)] transition-colors">{r.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
